<script>
export default {
  name: 'Dropzone',
  props: {
    name: String,
    required: Boolean,
    disabled: Boolean,
    accept: String,
    multiple: Boolean,
    maxSize: {
      type: Number,
      default: 10 * 1024 * 1024, // 10MB
    },
  },
  data() {
    return {
      files: [],
      active: false,
      error: null,
    };
  },
  emits: ['change'],
  mounted() {
    const input = this.$refs.fileinput;
    if (input && input.form) {
      input.form.addEventListener('reset', this.reset);
    }
  },
  beforeUnmount() {
    const input = this.$refs.fileinput;
    if (input && input.form) {
      input.form.removeEventListener('reset', this.reset);
    }
  },
  methods: {
    syncInputFilesFromArray() {
      const input = this.$refs.fileinput;
      if (!input) return;

      const dt = new DataTransfer();
      for (let i = 0; i < this.files.length; i++) {
        dt.items.add(this.files[i]);
      }
      input.files = dt.files;
    },

    dragover(e) {
      if (this.disabled) return;

      const fileItems = [...e.dataTransfer.items].filter((item) => item.kind === 'file');
      if (fileItems.length === 0) return;

      e.preventDefault();
      this.active = true;

      // Keep this simple: allow copy if count fits; validate accept on drop/change.
      const fitsCount = this.multiple || fileItems.length === 1;
      e.dataTransfer.dropEffect = fitsCount ? 'copy' : 'none';
    },

    dragleave() {
      this.active = false;
    },

    drop(e) {
      if (this.disabled) return;

      e.preventDefault();
      this.active = false;
      this.error = null;

      const incoming = [...e.dataTransfer.files].filter((file) => {
        const isDuplicate = this.files.some((f) => f.name === file.name && f.size === file.size && f.lastModified === file.lastModified);
        return !isDuplicate && this.validateFile(file);
      });

      if (this.multiple) {
        this.files.push(...incoming);
      } else {
        this.files.splice(0, this.files.length, ...incoming.slice(0, 1));
      }

      this.syncInputFilesFromArray();
      this.$emit('change', this.files);
    },

    reset() {
      this.files.splice(0, this.files.length);
      this.error = null;

      const input = this.$refs.fileinput;
      if (input) {
        // Clearing value helps keep DOM/input state in sync
        input.value = '';
      }

      this.$emit('change', this.files);
    },

    onChange() {
      if (this.disabled) return;

      this.error = null;
      const input = this.$refs.fileinput;
      const picked = input ? Array.from(input.files || []) : [];
      const incoming = picked.filter((file) => {
        const isDuplicate = this.files.some((f) => f.name === file.name && f.size === file.size && f.lastModified === file.lastModified);
        return !isDuplicate && this.validateFile(file);
      });

      if (this.multiple) {
        this.files.push(...incoming);
      } else {
        this.files.splice(0, this.files.length, ...incoming.slice(0, 1));
      }

      this.syncInputFilesFromArray();
      this.$emit('change', this.files);
    },

    validateFile(file) {
      if (file.size > this.maxSize) {
        this.error = this.$t('dropzone.error.size', {
          name: file.name,
          maxSize: `${this.maxSize / 1024 / 1024}MB`,
        });
        return false;
      }

      if (this.accept) {
        const acceptedTypes = this.accept.split(',').map((t) => t.trim().toLowerCase());
        const fileName = file.name.toLowerCase();
        const fileType = file.type.toLowerCase();

        const isAccepted = acceptedTypes.some((type) => {
          if (type.startsWith('.')) {
            return fileName.endsWith(type);
          }
          if (type.endsWith('/*')) {
            return fileType.startsWith(type.replace('/*', ''));
          }
          return fileType === type;
        });

        if (!isAccepted) {
          this.error = this.$t('dropzone.error.type', {
            name: file.name,
            accept: this.accept,
          });
          return false;
        }
      }

      return true;
    },

    removeFile(fileIndex) {
      if (this.disabled) return;

      this.files.splice(fileIndex, 1);
      this.syncInputFilesFromArray();

      this.$emit('change', this.files);
    },
  },
};
</script>

<template>
  <div class="dropzone" :class="{ 'is-active': active, 'is-disabled': disabled }">
    <label
      :for="'file-' + name"
      @dragover="dragover"
      @dragleave="dragleave"
      @drop="drop"
    >
      <span>
        <span v-html="$t('dropzone.label')"></span> <span class="icon-upload"></span>
      </span>
      <input
        type="file"
        :id="'file-' + name"
        ref="fileinput"
        :name="name"
        :accept="accept"
        :disabled="disabled"
        :multiple="multiple"
        @change="onChange"
      >
    </label>

    <div class="file-list" v-if="files.length">
      <div v-for="(file, index) in files" :key="file.name + '-' + index" class="file-item">
        <span class="file-name">{{ file.name }}</span>
        <button type="button" @click="removeFile(index)" class="btn-remove" :title="$t('dropzone.remove', { name: file.name })">&times;</button>
      </div>
    </div>

    <div v-if="error" class="error-message">
      {{ error }}
    </div>
  </div>
</template>

<style scoped lang="scss">
label {
  display: flex;
  font-weight: normal;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 1em;
  border: 2px dashed #ced4da;
  border-radius: .5em;
  color: #495057;
  background-color: #efefef;
  cursor: pointer;
  min-height: 7em;
}

input[type="file"] {
  display: none;
}

.has-error label {
  border-color: red;
}

.dropzone {
  display: flex;
  flex-direction: column;
  padding: 0;

  &.is-active label {
    border-color: #007bff;
    background-color: #e9ecef;
  }

  &.is-disabled label {
    cursor: not-allowed;
    opacity: 0.6;
  }
}

.file-list {
  margin-top: 0.5em;
  display: flex;
  flex-direction: column;
  gap: 0.25em;
}

.file-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #f8f9fa;
  padding: 0.25em 0.5em;
  border-radius: 4px;
  font-size: 0.9em;
}

.btn-remove {
  background: none;
  border: none;
  color: #dc3545;
  font-size: 1.2em;
  cursor: pointer;
  line-height: 1;
  padding: 0 0.2em;

  &:hover {
    color: #a71d2a;
  }
}

.error-message {
  color: #dc3545;
  font-size: 0.875em;
  margin-top: 0.25rem;
}
</style>
